import type { PropertyValues } from 'lit'
import type { ResizableOrientation, ResizablePanelConstraint } from './_resizable-layout.js'
import type { ResizableRegistrationContext } from './_ResizableContext.js'
import type { ResizableLayoutItem, ResizableResizeSource } from './ResizableEvents.js'
import { ContextProvider } from '@lit/context'
import { LitElement } from 'lit'

import { customElement, property } from 'lit/decorators.js'
import { injectLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import { immutableResizableLayout } from './_immutable-resizable-layout.js'
import { collapsePanelLayout, expandPanelLayout, keyboardResizeIntent, normalizeResizableLayout, pointerDeltaToPercent, primaryPanelRangeAtHandle, resizeAtHandle, resizeAtHandleWithCollapse, resizePanelLayout, resizePrimaryPanelAtHandle } from './_resizable-layout.js'
import { resizableContext } from './_ResizableContext.js'
import style from './Resizable.light-dom.css'
import {
  ResizableResizeEndEvent,
  ResizableResizeEvent,

  ResizableResizeStartEvent,
} from './ResizableEvents.js'
import ResizableHandle from './ResizableHandle.js'
import ResizablePanel from './ResizablePanel.js'

export type { ResizableOrientation } from './_resizable-layout.js'

const scopeKey = 'nord-resizable'
const equalityTolerance = 0.000001

interface DocumentUserSelectLock {
  count: number
  previousUserSelect: string
}

const documentUserSelectLocks = new WeakMap<Document, DocumentUserSelectLock>()

function acquireDocumentUserSelectLock(document: Document) {
  const activeLock = documentUserSelectLocks.get(document)
  if (activeLock) {
    activeLock.count++
    return
  }

  const documentElement = document.documentElement
  documentUserSelectLocks.set(document, {
    count: 1,
    previousUserSelect: documentElement.style.userSelect,
  })
  documentElement.style.userSelect = 'none'
}

function releaseDocumentUserSelectLock(document: Document) {
  const activeLock = documentUserSelectLocks.get(document)
  if (!activeLock) {
    return
  }
  activeLock.count--
  if (activeLock.count > 0) {
    return
  }

  documentUserSelectLocks.delete(document)
  if (activeLock.previousUserSelect) {
    document.documentElement.style.userSelect = activeLock.previousUserSelect
  }
  else {
    document.documentElement.style.removeProperty('user-select')
  }
}

interface Composition {
  panels: ResizablePanel[]
  handles: ResizableHandle[]
  constraints: ResizablePanelConstraint[]
}

interface ActiveDrag {
  document: Document
  handle: ResizableHandle
  handles: readonly ResizableHandle[]
  panels: readonly ResizablePanel[]
  pointerId: number
  handleIndex: number
  orientation: ResizableOrientation
  startCoordinate: number
  lastCoordinate: number
  axisSize: number
  startLayout: readonly number[]
  lastLayout: readonly number[]
  constraints: readonly ResizablePanelConstraint[]
  view: Window | null
}

/**
 * Resizable coordinates an alternating sequence of panels and handles using
 * immutable percentage layouts and native pointer/keyboard interactions.
 *
 * @status draft
 * @category layout
 * @lightDom true
 *
 * @slot - Alternating Resizable Panel and Resizable Handle direct children.
 *
 * @localization panelCollapsed - Announces the labeled primary panel's collapsed state.
 *
 * @fires {ResizableResizeStartEvent} nord-resize-start - Cancelable event dispatched before a pointer drag starts.
 * @fires {ResizableResizeEvent} nord-resize - Cancelable event carrying an immutable ordered layout and interaction source.
 * @fires {ResizableResizeEndEvent} nord-resize-end - Event dispatched whenever an accepted pointer drag terminates.
 *
 * @cssprop [--n-resizable-inline-size=100%] - Controls the group inline size.
 * @cssprop [--n-resizable-block-size=100%] - Controls the group block size.
 */
@customElement('nord-resizable')
export default class Resizable extends DraftComponentMixin(LitElement) {
  /** Axis along which panels resize. */
  @property({ reflect: true }) orientation: ResizableOrientation = 'horizontal'

  /** Controlled ordered percentage layout. Set through JavaScript. */
  @property({ attribute: false }) layout?: readonly number[]

  /** Initial uncontrolled ordered percentage layout. Set through JavaScript. */
  @property({ attribute: false }) defaultLayout?: readonly number[]

  /** Percentage changed by an orientation-appropriate Arrow key. */
  @property({ attribute: 'keyboard-step', reflect: true, type: Number }) keyboardStep = 5

  /** Percentage changed by Shift plus an orientation-appropriate Arrow key. */
  @property({ attribute: 'large-keyboard-step', reflect: true, type: Number }) largeKeyboardStep = 10

  /** Disables every handle in the group. */
  @property({ reflect: true, type: Boolean }) disabled = false

  private _currentLayout: readonly ResizableLayoutItem[] = Object.freeze([])
  private panels: ResizablePanel[] = []
  private handles: ResizableHandle[] = []
  private constraints: ResizablePanelConstraint[] = []
  private registeredPanels = new Set<ResizablePanel>()
  private registeredHandles = new Set<ResizableHandle>()
  private childObserver?: MutationObserver
  private resizeObserver?: ResizeObserver
  private connectionGeneration = 0
  private reconcileQueued = false
  private activeDrag?: ActiveDrag
  private restoreSizes = new Map<string, number>()
  private lastControlledProposal?: string
  private lastDiagnostic?: string
  private compositionValid = false
  private localization = new LocalizeController<'nord-resizable'>(this)

  private registration: ResizableRegistrationContext = {
    registerPanel: panel => this.registerPanel(panel),
    unregisterPanel: panel => this.unregisterPanel(panel),
    registerHandle: handle => this.registerHandle(handle),
    unregisterHandle: handle => this.unregisterHandle(handle),
    notifyPanel: panel => this.notifyChild(panel),
    notifyHandle: handle => this.notifyChild(handle),
    pointerDown: (handle, event) => this.startPointerResize(handle, event),
    pointerMove: (handle, event) => this.movePointerResize(handle, event),
    pointerEnd: (handle, event) => this.endPointerResize(handle, event),
    keyboardResize: (handle, event) => this.handleKeyboardResize(handle, event),
    cancelHandleInteraction: handle => this.cancelHandleInteraction(handle),
  }

  private provider = new ContextProvider(this, {
    context: resizableContext,
    initialValue: this.registration,
  })

  /** Current rendered layout in DOM panel order. The array and records are immutable. */
  get currentLayout(): readonly ResizableLayoutItem[] {
    return this._currentLayout
  }

  connectedCallback() {
    super.connectedCallback()
    this.connectionGeneration++
    injectLightDomStyles(this, style, scopeKey)
    this.childObserver = new MutationObserver(() => this.queueReconcile())
    this.childObserver.observe(this, { childList: true })
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.handleGroupResize())
      this.resizeObserver.observe(this)
    }
    this.queueReconcile()
  }

  disconnectedCallback() {
    this.connectionGeneration++
    this.finishPointerResize()
    this.childObserver?.disconnect()
    this.childObserver = undefined
    this.resizeObserver?.disconnect()
    this.resizeObserver = undefined
    super.disconnectedCallback()
  }

  protected createRenderRoot() {
    return this
  }

  protected updated(changedProperties: PropertyValues<this>) {
    if (changedProperties.has('layout')) {
      this.lastControlledProposal = undefined
    }
    if (changedProperties.has('disabled') && this.disabled) {
      this.finishPointerResize()
    }
    this.reconcile()
  }

  /** Returns the same immutable ordered records exposed by `currentLayout`. */
  getLayout(): readonly ResizableLayoutItem[] {
    return this.currentLayout
  }

  /** Validates and proposes or commits a complete ordered percentage layout. */
  setLayout(layout: readonly number[]): boolean {
    const normalized = normalizeResizableLayout(layout, this.constraints)
    return normalized ? this.proposeLayout(normalized, 'programmatic') : false
  }

  /** Resizes one panel by stable panel id and redistributes to nearest siblings. */
  resizePanel(panelId: string, size: number): boolean {
    const index = this.panelIndex(panelId)
    const next = resizePanelLayout(this.currentSizes(), this.constraints, index, size)
    return next ? this.proposeLayout(next, 'programmatic') : false
  }

  /** Collapses one collapsible panel atomically. */
  collapsePanel(panelId: string): boolean {
    const index = this.panelIndex(panelId)
    const transaction = collapsePanelLayout(this.currentSizes(), this.constraints, index)
    if (!transaction || this.sameSizes(transaction.layout, this.currentSizes())) {
      return false
    }
    const accepted = this.proposeLayout(transaction.layout, 'programmatic')
    if (accepted) {
      this.restoreSizes.set(panelId, transaction.restoreSize)
    }
    return accepted
  }

  /** Restores one collapsible panel to its latest expanded size. */
  expandPanel(panelId: string): boolean {
    const index = this.panelIndex(panelId)
    const next = expandPanelLayout(
      this.currentSizes(),
      this.constraints,
      index,
      this.restoreSizes.get(panelId),
    )
    return next ? this.proposeLayout(next, 'programmatic') : false
  }

  private registerPanel(panel: ResizablePanel) {
    if (!this.acceptDirectChild(panel)) {
      return
    }
    this.registeredPanels.add(panel)
    this.queueReconcile()
  }

  private unregisterPanel(panel: ResizablePanel) {
    this.registeredPanels.delete(panel)
    this.queueReconcile()
  }

  private registerHandle(handle: ResizableHandle) {
    if (!this.acceptDirectChild(handle)) {
      return
    }
    this.registeredHandles.add(handle)
    this.queueReconcile()
  }

  private unregisterHandle(handle: ResizableHandle) {
    this.registeredHandles.delete(handle)
    this.cancelHandleInteraction(handle)
    this.queueReconcile()
  }

  private notifyChild(child: ResizablePanel | ResizableHandle) {
    if (child.parentElement === this) {
      this.queueReconcile()
    }
  }

  private acceptDirectChild(child: HTMLElement): boolean {
    if (child.parentElement === this) {
      return true
    }
    this.diagnose('Resizable panels and handles must be direct children of nord-resizable.')
    return false
  }

  private queueReconcile() {
    if (this.reconcileQueued) {
      return
    }
    this.reconcileQueued = true
    queueMicrotask(() => {
      this.reconcileQueued = false
      if (this.isConnected) {
        this.reconcile()
      }
    })
  }

  private readComposition(): Composition | undefined {
    const children = Array.from(this.children)
    const panels: ResizablePanel[] = []
    const handles: ResizableHandle[] = []
    if (children.length === 0 || children.length % 2 === 0) {
      this.diagnose('nord-resizable requires an alternating panel/handle/panel sequence.')
      return undefined
    }

    for (const [index, child] of children.entries()) {
      if (index % 2 === 0 && child instanceof ResizablePanel) {
        panels.push(child)
      }
      else if (index % 2 === 1 && child instanceof ResizableHandle) {
        handles.push(child)
      }
      else {
        this.diagnose('nord-resizable requires an alternating panel/handle/panel sequence with no unsupported direct children.')
        return undefined
      }
    }

    const constraints = panels.map(panel => ({
      id: this.panelIdentifier(panel),
      defaultSize: panel.defaultSize,
      minSize: panel.minSize,
      maxSize: panel.maxSize,
      collapsible: panel.collapsible,
      collapsedSize: panel.collapsedSize,
    }))
    return { panels, handles, constraints }
  }

  private reconcile() {
    const composition = this.readComposition()
    if (!composition) {
      this.compositionValid = false
      this.finishPointerResize()
      this.applyInvalidComposition()
      return
    }
    const { panels, handles, constraints } = composition
    if (this.pointerDragInvalidatedBy(composition)) {
      this.finishPointerResize()
    }
    const previousById = new Map(this.currentLayout.map(({ id, size }) => [id, size]))
    const requested = this.layout !== undefined
      ? this.layout
      : this.currentLayout.length === 0
        ? this.defaultLayout
        : panels.map(panel => previousById.get(this.panelIdentifier(panel)) ?? panel.defaultSize)
    const normalized = normalizeResizableLayout(requested, constraints)
    if (!normalized) {
      this.compositionValid = false
      this.finishPointerResize()
      this.diagnose('nord-resizable received invalid or unsatisfiable panel sizes; the last valid layout was retained.')
      this.applyInvalidComposition(panels, handles)
      return
    }

    this.lastDiagnostic = undefined
    this.panels = panels
    this.handles = handles
    this.constraints = constraints
    this.compositionValid = true
    this.applyLayout(normalized)
  }

  private pointerDragInvalidatedBy(composition: Composition): boolean {
    const drag = this.activeDrag
    if (!drag) {
      return false
    }

    const samePanels = drag.panels.length === composition.panels.length
      && drag.panels.every((panel, index) => panel === composition.panels[index])
    const sameHandles = drag.handles.length === composition.handles.length
      && drag.handles.every((handle, index) => handle === composition.handles[index])
    const sameConstraints = drag.constraints.length === composition.constraints.length
      && drag.constraints.every((constraint, index) => {
        const next = composition.constraints[index]
        return constraint.id === next.id
          && constraint.defaultSize === next.defaultSize
          && constraint.minSize === next.minSize
          && constraint.maxSize === next.maxSize
          && constraint.collapsible === next.collapsible
          && constraint.collapsedSize === next.collapsedSize
      })

    return this.disabled
      || drag.handle.interactionDisabled
      || drag.orientation !== this.orientation
      || !samePanels
      || !sameHandles
      || !sameConstraints
  }

  private applyInvalidComposition(
    panels: readonly ResizablePanel[] = Array.from(this.children).filter(
      (child): child is ResizablePanel => child instanceof ResizablePanel,
    ),
    handles: readonly ResizableHandle[] = Array.from(this.children).filter(
      (child): child is ResizableHandle => child instanceof ResizableHandle,
    ),
  ) {
    if (this.currentLayout.length === 0) {
      panels.forEach((panel) => {
        panel.style.flexGrow = '1'
      })
    }
    handles.forEach(handle => handle.setGroupState({
      disabled: true,
      orientation: this.orientation,
    }))
  }

  private applyLayout(sizes: readonly number[]) {
    this._currentLayout = immutableResizableLayout(this.panels.map((panel, index) => ({
      id: this.panelIdentifier(panel),
      size: sizes[index],
    })))

    this.panels.forEach((panel, index) => {
      const constraint = this.constraints[index]
      const collapsedSize = constraint.collapsedSize ?? 0
      const collapsed = Boolean(
        constraint.collapsible
        && Math.abs(sizes[index] - collapsedSize) <= equalityTolerance,
      )
      panel.style.flexGrow = String(sizes[index])
      panel.toggleAttribute('data-collapsed', collapsed)
      if (!collapsed && sizes[index] >= (constraint.minSize ?? 0) - equalityTolerance) {
        this.restoreSizes.set(constraint.id, sizes[index])
      }
    })

    this.handles.forEach((handle, index) => {
      const before = this.panels[index]
      const after = this.panels[index + 1]
      const constraint = this.constraints[index]
      const size = sizes[index]
      const collapsed = Boolean(
        constraint.collapsible
        && Math.abs(size - (constraint.collapsedSize ?? 0)) <= equalityTolerance,
      )
      const label = this.panelLabel(before)
      const range = primaryPanelRangeAtHandle(sizes, this.constraints, index)
      handle.setGroupState({
        disabled: this.disabled,
        orientation: this.orientation,
        controls: `${before.id} ${after.id}`,
        valueMin: range?.min,
        valueMax: range?.max,
        valueNow: range?.now,
        valueText: collapsed ? this.localization.term('panelCollapsed', label) : undefined,
      })
    })
  }

  private diagnose(message: string) {
    if (message === this.lastDiagnostic) {
      return
    }
    this.lastDiagnostic = message
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`NORD: ${message}`)
    }
  }

  private panelIdentifier(panel: ResizablePanel): string {
    return panel.panelId?.trim() || panel.id
  }

  private panelLabel(panel: ResizablePanel): string {
    const label = panel.getAttribute('aria-label')?.trim() || this.panelIdentifier(panel)
    return label.charAt(0).toUpperCase() + label.slice(1).replace(/-/g, ' ')
  }

  private panelIndex(panelId: string): number {
    return this.constraints.findIndex(({ id }) => id === panelId)
  }

  private currentSizes(): readonly number[] {
    return this.currentLayout.map(({ size }) => size)
  }

  private sameSizes(left: readonly number[], right: readonly number[]): boolean {
    return left.length === right.length && left.every(
      (size, index) => Math.abs(size - right[index]) <= equalityTolerance,
    )
  }

  private recordsFor(sizes: readonly number[]): readonly ResizableLayoutItem[] {
    return immutableResizableLayout(this.constraints.map(({ id }, index) => ({ id, size: sizes[index] })))
  }

  private signature(sizes: readonly number[]): string {
    return sizes.join(',')
  }

  private proposeLayout(next: readonly number[], source: ResizableResizeSource): boolean {
    if (!this.compositionValid) {
      return false
    }
    const current = this.currentSizes()
    if (this.sameSizes(next, current)) {
      return false
    }
    const controlled = this.layout !== undefined
    const signature = this.signature(next)
    if (controlled && signature === this.lastControlledProposal) {
      return false
    }

    const event = new ResizableResizeEvent(this.recordsFor(next), source)
    if (!this.dispatchEvent(event)) {
      return false
    }
    if (controlled) {
      this.lastControlledProposal = signature
    }
    else {
      this.applyLayout(next)
    }
    return true
  }

  private handleIndex(handle: ResizableHandle): number {
    return handle.parentElement === this ? this.handles.indexOf(handle) : -1
  }

  private handleKeyboardResize(handle: ResizableHandle, event: KeyboardEvent) {
    const index = this.handleIndex(handle)
    if (index < 0 || handle.interactionDisabled || this.disabled) {
      return
    }
    const direction = getComputedStyle(this).direction === 'rtl' ? 'rtl' : 'ltr'
    const intent = keyboardResizeIntent(
      event.key,
      event.shiftKey,
      this.orientation,
      direction,
      this.keyboardStep,
      this.largeKeyboardStep,
    )
    if (!intent) {
      return
    }

    let next: readonly number[] | undefined
    const current = this.currentSizes()
    if (intent.kind === 'delta') {
      let keyboardStart = current
      const growingPanelIndex = intent.delta > 0 ? index : index + 1
      const growingConstraint = this.constraints[growingPanelIndex]
      const growingPanelIsCollapsed = Boolean(
        growingConstraint?.collapsible
        && Math.abs(
          current[growingPanelIndex] - (growingConstraint.collapsedSize ?? 0),
        ) <= equalityTolerance,
      )
      if (growingConstraint && growingPanelIsCollapsed) {
        keyboardStart = expandPanelLayout(
          current,
          this.constraints,
          growingPanelIndex,
          this.restoreSizes.get(growingConstraint.id),
        ) ?? current
      }
      next = resizeAtHandle(keyboardStart, this.constraints, index, intent.delta)
    }
    else if (intent.kind === 'minimum') {
      const constraint = this.constraints[index]
      const target = constraint.collapsible
        ? constraint.collapsedSize ?? 0
        : constraint.minSize ?? 0
      next = resizePrimaryPanelAtHandle(current, this.constraints, index, target)
      if (
        constraint.collapsible
        && next
        && Math.abs(next[index] - (constraint.collapsedSize ?? 0)) <= equalityTolerance
        && Math.abs(current[index] - next[index]) > equalityTolerance
      ) {
        this.restoreSizes.set(constraint.id, current[index])
      }
    }
    else if (intent.kind === 'maximum') {
      next = resizePrimaryPanelAtHandle(
        current,
        this.constraints,
        index,
        this.constraints[index].maxSize ?? 100,
      )
    }
    else {
      const constraint = this.constraints[index]
      const collapsed = Math.abs(current[index] - (constraint.collapsedSize ?? 0)) <= equalityTolerance
      if (constraint.collapsible && collapsed) {
        next = resizePrimaryPanelAtHandle(
          current,
          this.constraints,
          index,
          this.restoreSizes.get(constraint.id)
          ?? constraint.defaultSize
          ?? constraint.minSize
          ?? constraint.collapsedSize
          ?? 0,
        )
      }
      else if (constraint.collapsible) {
        next = resizePrimaryPanelAtHandle(
          current,
          this.constraints,
          index,
          constraint.collapsedSize ?? 0,
        )
        if (
          next
          && Math.abs(next[index] - (constraint.collapsedSize ?? 0)) <= equalityTolerance
        ) {
          this.restoreSizes.set(constraint.id, current[index])
        }
      }
    }

    if (next) {
      this.proposeLayout(next, 'keyboard')
    }
    event.preventDefault()
  }

  private startPointerResize(handle: ResizableHandle, event: PointerEvent) {
    const handleIndex = this.handleIndex(handle)
    if (
      this.activeDrag
      || handleIndex < 0
      || handle.interactionDisabled
      || this.disabled
      || event.button !== 0
      || !event.isPrimary
    ) {
      return
    }
    const orientation = this.orientation
    const axisSize = this.usableAxisSize()
    const startCoordinate = orientation === 'horizontal' ? event.clientX : event.clientY
    if (axisSize === undefined || !Number.isFinite(startCoordinate)) {
      return
    }
    try {
      handle.setPointerCapture(event.pointerId)
    }
    catch {
      return
    }

    const source = 'pointer'
    const connectionGeneration = this.connectionGeneration
    const startEvent = new ResizableResizeStartEvent(this.currentLayout, source)
    if (!this.dispatchEvent(startEvent)) {
      this.safelyReleasePointerCapture(handle, event.pointerId)
      return
    }
    const pointerStartIsValid = () => {
      let stillCaptured = false
      try {
        stillCaptured = handle.hasPointerCapture(event.pointerId)
      }
      catch {
        // Removing or replacing a handle can make capture queries invalid.
      }
      return this.isConnected
        && this.connectionGeneration === connectionGeneration
        && handle.isConnected
        && this.handleIndex(handle) === handleIndex
        && this.orientation === orientation
        && !handle.interactionDisabled
        && !this.disabled
        && stillCaptured
    }
    if (!pointerStartIsValid()) {
      this.safelyReleasePointerCapture(handle, event.pointerId)
      return
    }

    // Preventing the pointer event suppresses the browser's normal click-to-focus
    // behavior. Restore it explicitly so a pointer user can immediately continue
    // resizing with the keyboard.
    handle.focus({ preventScroll: true })
    if (!pointerStartIsValid()) {
      this.safelyReleasePointerCapture(handle, event.pointerId)
      return
    }

    const document = this.ownerDocument
    const view = document.defaultView
    acquireDocumentUserSelectLock(document)
    this.activeDrag = {
      document,
      handle,
      handles: [...this.handles],
      panels: [...this.panels],
      pointerId: event.pointerId,
      handleIndex,
      orientation,
      startCoordinate,
      lastCoordinate: startCoordinate,
      axisSize,
      startLayout: this.currentSizes(),
      lastLayout: this.currentSizes(),
      constraints: this.constraints.map(constraint => ({ ...constraint })),
      view,
    }
    this.toggleAttribute('data-resizing', true)
    handle.toggleAttribute('data-resizing', true)
    view?.addEventListener('blur', this.handleWindowBlur)
    event.preventDefault()
  }

  private movePointerResize(handle: ResizableHandle, event: PointerEvent) {
    const drag = this.activeDrag
    if (!drag || drag.handle !== handle || drag.pointerId !== event.pointerId) {
      return
    }
    const coordinate = this.orientation === 'horizontal' ? event.clientX : event.clientY
    drag.lastCoordinate = coordinate
    const direction = getComputedStyle(this).direction === 'rtl' ? 'rtl' : 'ltr'
    const delta = pointerDeltaToPercent(
      drag.startCoordinate,
      coordinate,
      drag.axisSize,
      this.orientation,
      direction,
    )
    if (delta === undefined) {
      return
    }
    const restoreSizes = Object.fromEntries(this.restoreSizes)
    const transaction = resizeAtHandleWithCollapse(
      drag.startLayout,
      drag.constraints,
      drag.handleIndex,
      delta,
      restoreSizes,
    )
    if (!transaction) {
      return
    }
    const accepted = this.proposeLayout(transaction.layout, 'pointer')
    if (accepted) {
      drag.lastLayout = transaction.layout
      if (transaction.collapsedPanelId && transaction.restoreSize !== undefined) {
        this.restoreSizes.set(transaction.collapsedPanelId, transaction.restoreSize)
      }
    }
    event.preventDefault()
  }

  private endPointerResize(handle: ResizableHandle, event: PointerEvent) {
    const drag = this.activeDrag
    if (!drag || drag.handle !== handle || drag.pointerId !== event.pointerId) {
      return
    }
    this.finishPointerResize()
  }

  private cancelHandleInteraction(handle: ResizableHandle) {
    if (this.activeDrag?.handle === handle) {
      this.finishPointerResize()
    }
  }

  private finishPointerResize() {
    const drag = this.activeDrag
    if (!drag) {
      return
    }
    this.activeDrag = undefined
    drag.view?.removeEventListener('blur', this.handleWindowBlur)
    this.removeAttribute('data-resizing')
    drag.handle.removeAttribute('data-resizing')
    releaseDocumentUserSelectLock(drag.document)
    this.safelyReleasePointerCapture(drag.handle, drag.pointerId)
    this.dispatchEvent(new ResizableResizeEndEvent(this.recordsFor(drag.lastLayout), 'pointer'))
  }

  private handleWindowBlur = () => this.finishPointerResize()

  private handleGroupResize() {
    const drag = this.activeDrag
    if (!drag) {
      return
    }
    const axisSize = this.usableAxisSize()
    if (axisSize === undefined || Math.abs(axisSize - drag.axisSize) <= equalityTolerance) {
      return
    }
    drag.axisSize = axisSize
    drag.startCoordinate = drag.lastCoordinate
    drag.startLayout = drag.lastLayout
  }

  private usableAxisSize(): number | undefined {
    const rect = this.getBoundingClientRect()
    const groupSize = this.orientation === 'horizontal' ? rect.width : rect.height
    const handleSize = this.handles.reduce((total, handle) => {
      const handleRect = handle.getBoundingClientRect()
      return total + (this.orientation === 'horizontal' ? handleRect.width : handleRect.height)
    }, 0)
    const usableSize = groupSize - handleSize
    return Number.isFinite(usableSize) && usableSize > 0 ? usableSize : undefined
  }

  private safelyReleasePointerCapture(handle: ResizableHandle, pointerId: number) {
    try {
      if (handle.hasPointerCapture(pointerId)) {
        handle.releasePointerCapture(pointerId)
      }
    }
    catch {
      // Capture can already be gone for lostpointercapture/disconnect paths.
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-resizable': Resizable
  }
}
